import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import { getSessionPatientDocumentLinks } from '@/pages/api/scribeAPIV2';
import type { SessionPatientDocumentLinkResponse } from '@heidi/session/contracts';
import { GET_SESSION_PATIENT_DOCUMENT_LINKS } from '@heidi/session/contracts';
import { useQuery } from '@tanstack/react-query';

// The key's single definition site moved to `@heidi/session/contracts` on
// 2026-08-15 (ARCFE-1076): the program owner resolved
// `session-patient-document-link-owner` in SESSION's favour on the
// resource-owner rule, so the identity now sits with the domain that owns the
// resource rather than "regardless of which lane ends up owning the hook".
// THE HOOK ITSELF IS UNTOUCHED and stays app-bodied — this line is the only
// change, and it is a door swap, not a move. Re-exported, not re-declared:
// existing importers keep resolving here.
export { GET_SESSION_PATIENT_DOCUMENT_LINKS };

/**
 * Fetch the patient documents currently linked to a session as note-gen context.
 *
 * Each item already carries the embedded patient-document metadata
 * (file_name, mime_type, size_bytes, description, summary) so the FE can
 * render a row without a second query for the underlying doc.
 *
 * The hook is enabled only when the user is authenticated and a sessionId is
 * present; pass `null`/`undefined` for sessionId to keep the query idle.
 */
export function useQuerySessionPatientDocumentLinks(
  sessionId: string | null | undefined
) {
  const { isAuthenticated } = useIsAuthenticated();

  return useQuery<SessionPatientDocumentLinkResponse[]>({
    queryKey: [GET_SESSION_PATIENT_DOCUMENT_LINKS, { sessionId }],
    enabled: isAuthenticated && !!sessionId,
    queryFn: async () => {
      if (!sessionId) return [];
      const { data } = await getSessionPatientDocumentLinks(sessionId);
      return data.items;
    },
    // Embedded patient-document fields (file_name, description, summary)
    // are PHI; React Query's default `staleTime: 0` would refetch this on
    // every window-focus and reconnect. 60s keeps the data fresh enough
    // for the dialog/badge while cutting unnecessary PHI exposure.
    staleTime: 60_000,
  });
}
